import React, { useEffect, useRef, useState } from 'react';
import { FormControl, FormLabel } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch } from '../store';
import { setGameIdForModal } from '../reducers/uiReducer';
import CommentsAPI from '../api/CommentsAPI';
import { CommentSearchResultModel } from '../types/CommentModel';

// Full-text search over comments. Debounces input, shows a results dropdown, and opens
// the relevant game overlay when a result is clicked.
export default function GamesCommentSearch(): React.ReactNode {
  const dispatch = useAppDispatch();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CommentSearchResultModel[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const data = await CommentsAPI.search(term, 20);
        setResults(data ?? []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  // Close the dropdown when clicking outside.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const openGame = (gameId: number) => {
    dispatch(setGameIdForModal(gameId));
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={containerRef} className="cm-comment-search position-relative">
      <FormLabel>Search comments</FormLabel>
      <div className="cm-comment-search-field">
        <FontAwesomeIcon icon={faMagnifyingGlass} className="cm-comment-search-icon" />
        <FormControl
          className="cm-comment-search-input"
          value={query}
          placeholder="Search comments..."
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (results.length > 0) {
              setOpen(true);
            }
          }}
        />
      </div>
      {open ? (
        <div className="cm-comment-search-results">
          {loading ? (
            <div className="cm-comment-search-empty text-muted small">Searching...</div>
          ) : results.length === 0 ? (
            <div className="cm-comment-search-empty text-muted small">No matches.</div>
          ) : (
            results.map((r) => (
              <button
                type="button"
                key={r.CommentId}
                className="cm-comment-search-item"
                onClick={() => openGame(r.GameId)}
              >
                <span className="cm-comment-search-game">{r.GameLabel}</span>
                <span className="cm-comment-search-excerpt">{r.Excerpt}</span>
                <span className="cm-comment-search-meta text-muted small">
                  {r.AuthorName} · {new Date(r.CreatedAt).toLocaleDateString()}
                </span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
